// @flow
import {useState} from 'react';
import type {Node} from 'react';
import {useNavigate} from 'react-router-dom';
import {Box, DataGrid, Paper} from '@wellstone-solutions/web';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useProgramAdmissionsList} from '../../queries/useProgramAdmissionsList';
import {
  RowCount,
  getSortModel,
  usePaginationParams,
  DEFAULT_PAGE,
} from 'modules/datagrid';
import {useStores} from 'hooks/useStores';
import {memberListColumns} from './columns';
import {ResendInvitationModal} from './ResendInvitationModal';
import {ProgramSelect} from 'components/ProgramSelect';

export const AllMembersTab = (): Node => {
  const {meStore} = useStores();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [memberToInvite, setMemberToInvite] = useState(null);

  const defaultSortModel = [
    {
      field: 'member__name',
      sort: 'asc',
    },
  ];

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    ordering,
    setOrdering,
    filters,
    setFilters,
  } = usePaginationParams({
    defaultSortModel,
  });

  const query = useProgramAdmissionsList({
    ...(filters?.programId && {programId: filters.programId}),
    page,
    pageSize,
    ordering,
    distinct: true,
  });

  const columns = isMobile
    ? [memberListColumns.mobile]
    : [
        memberListColumns.name,
        memberListColumns.lastAppOpen,
        memberListColumns.startDate,
        memberListColumns.endDate,
      ];

  if (meStore.ehrIntegrationOption && !isMobile) {
    columns.push({
      ...memberListColumns.ehr,
      headerName:
        meStore.ehrIntegrationOption?.name ?? memberListColumns.ehr.headerName,
    });
  }

  const handleRowClick = ({row}, event) => {
    // Don't navigate if the user is selecting text
    if (window.getSelection()?.type === 'Range') {
      return;
    }

    navigate(`/members/${row.member.id}`);
  };

  columns.push(
    memberListColumns.getRowActions({onResendInvite: setMemberToInvite}),
  );

  return (
    <Box>
      <Box sx={{mb: 4}}>
        <ProgramSelect
          value={filters?.programId || null}
          onChange={(programId) => {
            setFilters({
              programId,
              page: DEFAULT_PAGE,
            });
          }}
        />
      </Box>

      <RowCount
        isLoading={query.isFetching}
        label="member"
        count={query.data?.count || 0}
      />

      <Paper elevation={1}>
        <DataGrid
          data-testid="member-list-datagrid"
          autoHeight
          variant="striped"
          density="comfortable"
          columns={columns}
          loading={query.isFetching}
          // $FlowFixMe - Datagrid types are wrong in Common Libs
          rows={query.data?.results || []}
          rowCount={query.data?.count || 0}
          paginationMode="server"
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          initialState={{
            sorting: {
              sortModel: getSortModel(ordering),
            },
          }}
          page={page - 1}
          pageSize={pageSize}
          onPageChange={(newPage) => {
            setPage(newPage + 1);
          }}
          onPageSizeChange={setPageSize}
          onSortModelChange={setOrdering}
          onRowClick={handleRowClick}
          sx={{
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
            },
          }}
          {...(isMobile && {
            headerHeight: 0,
            getRowHeight: () => 100,
            getRowClassName: (params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'odd' : 'even',
          })}
        />
      </Paper>

      <ResendInvitationModal
        member={memberToInvite}
        open={!!memberToInvite}
        onComplete={() => {
          setMemberToInvite(null);
        }}
      />
    </Box>
  );
};
